import { useState, useEffect, useCallback, useRef } from 'react';

import { ERROR_MESSAGES } from '../../constants';
import {
  formatNumericValue,
  parseNumericInput,
  cleanSearchInputWithSpecialCharacters,
} from '../../utils';
import { VehicleType, UsageType } from '../constants';

interface UseVehicleMatchmakerSearchParams {
  onSendMessage?: (message?: string) => void;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
  onProcessingStateChange?: (isProcessing: boolean) => void;
}

interface UseVehicleMatchmakerSearchReturn {
  budgetMin: string;
  budgetMax: string;
  vehicleType: VehicleType;
  usage: UsageType;
  description: string;
  error: string | null;
  hasSubmitted: boolean;
  handleBudgetMinChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBudgetMaxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleVehicleTypeChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null,
  ) => void;
  handleUsageChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null,
  ) => void;
  handleDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => Promise<void>;
}

/**
 * useVehicleMatchmakerSearch - Custom hook to manage vehicle matchmaker search logic
 * Handles user preferences and generates vehicle recommendations
 * @hook
 */
export const useVehicleMatchmakerSearch = ({
  onSendMessage,
  shouldResetInputs,
  onResetComplete,
  onProcessingStateChange,
}: UseVehicleMatchmakerSearchParams): UseVehicleMatchmakerSearchReturn => {
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [vehicleType, setVehicleType] = useState<VehicleType>('');
  const [usage, setUsage] = useState<UsageType>('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const isProcessingRef = useRef(false);

  // Reset all form states
  const resetAllStates = useCallback(() => {
    setBudgetMin('');
    setBudgetMax('');
    setVehicleType('');
    setUsage('');
    setDescription('');
    setError(null);
    setHasSubmitted(false);
    isProcessingRef.current = false;
    onProcessingStateChange?.(false);
  }, [onProcessingStateChange]);

  // Handle reset when shouldResetInputs becomes true
  useEffect(() => {
    if (shouldResetInputs) {
      resetAllStates();
      onResetComplete();
    }
  }, [shouldResetInputs, resetAllStates, onResetComplete]);

  const handleBudgetMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBudgetMin(formatNumericValue(e.target.value));
      setError(null);
    },
    [],
  );

  const handleBudgetMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBudgetMax(formatNumericValue(e.target.value));
      setError(null);
    },
    [],
  );

  const handleVehicleTypeChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: string | null) => {
      setVehicleType((value as VehicleType) || '');
      setError(null);
    },
    [],
  );

  const handleUsageChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: string | null) => {
      setUsage((value as UsageType) || '');
      setError(null);
    },
    [],
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDescription(cleanSearchInputWithSpecialCharacters(e.target.value));
      setError(null);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>): Promise<void> => {
      e?.preventDefault();

      if (!budgetMax || !onSendMessage) {
        setError(ERROR_MESSAGES.NO_BUDGET);
        return;
      }

      const minBudget = parseNumericInput(budgetMin);
      const maxBudget = parseNumericInput(budgetMax);

      if (maxBudget <= 0) {
        setError(ERROR_MESSAGES.NO_BUDGET);
        return;
      }

      if (maxBudget <= minBudget) {
        setError(ERROR_MESSAGES.INVALID_BUDGET);
        return;
      }

      setError(null);
      setHasSubmitted(true);

      // Build JSON prompt for the LLM
      const preferences = {
        budget_min: minBudget,
        budget_max: maxBudget,
        vehicle_type: vehicleType || null,
        usage: usage || null,
        description: description || null,
      };

      onSendMessage(JSON.stringify(preferences));
    },
    [
      budgetMin,
      budgetMax,
      vehicleType,
      usage,
      description,
      onSendMessage,
      onProcessingStateChange,
    ],
  );

  return {
    budgetMin,
    budgetMax,
    vehicleType,
    usage,
    description,
    error,
    hasSubmitted,
    handleBudgetMinChange,
    handleBudgetMaxChange,
    handleVehicleTypeChange,
    handleUsageChange,
    handleDescriptionChange,
    handleSubmit,
  };
};
